import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

export default function AboutPage() {
  const { t, i18n } = useTranslation();
  const { lang = 'en' } = useParams<{ lang: string }>();

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang, i18n]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <div className="pt-20 md:pt-24 pb-4 bg-white border-b border-background-200/70">
        <div className="container-main">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <a href={`/${lang}/`} className="hover:text-primary-600 transition-colors">{t('breadcrumb.home')}</a>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-700">About Us</span>
          </nav>
        </div>
      </div>

      <section className="bg-white py-16 md:py-20">
        <div className="container-main text-center">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
            About AllChinaBuy Spreadsheet Resource Hub
          </h1>
          <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed text-base md:text-lg">
            An independent informational platform helping buyers navigate the AllChinaBuy ecosystem. We provide guides, reviews, product discovery tools, and safety resources to make your buying journey smoother.
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="prose-content">
            <h2>Our Mission</h2>
            <p>
              AllChinaBuy Spreadsheet Resource Hub was created to help international buyers navigate the complex world of Chinese marketplace purchasing through buying agents. Our mission is to provide clear, accurate, and unbiased information that empowers buyers to make informed decisions about products, sellers, shipping, and safety. We believe that transparency and education are the best tools for a positive buying experience.
            </p>
            <p>
              Whether you are a first-time buyer curious about how the AllChinaBuy spreadsheet works, or an experienced shopper looking for the latest coupon codes and shipping tips, our goal is to be the most helpful and reliable resource available. We cover every aspect of the buying journey from product discovery to final delivery.
            </p>

            <h2>What We Offer</h2>
            <p>
              <strong>Product Discovery</strong> - We maintain an organized product catalog sourced from the AllChinaBuy spreadsheet, covering categories like shoes, hoodies, t-shirts, jackets, pants, accessories, bags, and more. Each listing includes pricing, images, and direct links to purchase pages.
            </p>
            <p>
              <strong>Guides & Tutorials</strong> - Our comprehensive guides cover spreadsheet navigation, seller verification, QC photo inspection, shipping method selection, and beginner buying workflows. These resources are designed to be practical and actionable.
            </p>
            <p>
              <strong>Reviews & Comparisons</strong> - We provide independent reviews of AllChinaBuy and compare it with other popular agents like Superbuy, CSSBuy, and Sugargoo. Our comparisons focus on feature availability, user experience, and practical considerations rather than subjective ratings.
            </p>
            <p>
              <strong>Safety Resources</strong> - Safety is a top priority. Our Safety Center covers payment security, seller verification, scam prevention, account protection, and what to do if something goes wrong. We want every buyer to feel confident and protected.
            </p>

            <h2>Important Disclaimer</h2>
            <p>
              This website is an <strong>independent informational resource</strong> and is not affiliated with, endorsed by, or the official website of AllChinaBuy. We do not process orders, handle payments, or provide direct customer support for AllChinaBuy transactions. All product links redirect to the main website for actual purchasing. For order-specific issues, refunds, or account problems, please contact AllChinaBuy directly through their official channels.
            </p>
            <p>
              We strive for accuracy in all information presented, but we cannot guarantee that all details (prices, availability, coupon codes, shipping rates) are current at all times. Prices and availability change frequently, and we recommend verifying all information directly on the AllChinaBuy website before making purchase decisions.
            </p>

            <h2>Editorial Standards</h2>
            <p>
              All content on this website is written independently based on research, publicly available information, and community feedback. We do not accept payment for favorable reviews or product placement. Our goal is to provide honest, useful information that genuinely helps buyers. If you find errors or outdated information, please contact us so we can correct it.
            </p>

            <h2>Our Values</h2>
            <p>
              <strong>Transparency</strong> - We clearly state what we know, what we do not know, and when information is based on community reports rather than direct verification.
            </p>
            <p>
              <strong>Independence</strong> - We are not affiliated with AllChinaBuy or any buying agent. Our recommendations are based on features and user experience, not financial incentives.
            </p>
            <p>
              <strong>Helpfulness</strong> - Every piece of content is created with the buyer experience in mind. If it does not help someone make a better decision, we do not publish it.
            </p>
            <p>
              <strong>Accuracy</strong> - We fact-check information and update content regularly to reflect changes in the AllChinaBuy ecosystem.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="bg-background-50 rounded-2xl p-8 md:p-10 border border-background-200/70 text-center">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
              Ready to Explore?
            </h3>
            <p className="text-foreground-600 mb-6 leading-relaxed">
              Start your journey with the AllChinaBuy spreadsheet, browse products, and discover everything this platform has to offer.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={`/${lang}/products/`} className="btn-primary inline-flex items-center justify-center">
                View Products <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="btn-secondary inline-flex items-center justify-center">
                Buy It Now <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}